import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DIALOG_FORM_CLASSES,
  DialogBody,
} from "@/components/ui/dialog";
import { useCloseGuard } from "@/hooks/use-close-guard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { otpSchema, type OtpForm } from "@/schemas/tpaygate.schema";
import { useConfirmOtp } from "@/hooks/useTPaygateQueries";
interface Props {
  configBankId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function OtpConfirmDialog({
  configBankId,
  open,
  onClose,
  onSuccess,
}: Props) {
  const confirmOtp = useConfirmOtp();

  const form = useForm<OtpForm>({
    resolver: zodResolver(otpSchema),
    defaultValues: { otpNumber: "" },
  });

  const handleSubmit = async (values: OtpForm) => {
    await confirmOtp.mutateAsync({
      configBankId,
      otpNumber: values.otpNumber,
    });
    form.reset();
    onSuccess();
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const guard = useCloseGuard({
    open,
    onOpenChange: (v) => !v && handleClose(),
    isDirty: form.formState.isDirty,
    isSubmitting: confirmOtp.isPending,
    onDiscard: () => form.reset(),
  });

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Enter OTP</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className={DIALOG_FORM_CLASSES}
            >
              <DialogBody className="space-y-4">
                <DialogDescription>
                  The bank sent a 6-digit OTP to the registered phone number.
                  Enter it below to complete the bank connection.
                </DialogDescription>
                <FormField
                  control={form.control}
                  name="otpNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>OTP Code</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="123456"
                          maxLength={6}
                          inputMode="numeric"
                          autoFocus
                          className="text-center tracking-widest text-lg font-mono"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </DialogBody>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => guard.onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={confirmOtp.isPending}>
                  {confirmOtp.isPending ? "Verifying…" : "Confirm"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      {guard.dialog}
    </>
  );
}
